import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';

@Injectable({
    providedIn: 'root'
})
export class TovpilCrudService {

    constructor(private apiService: ApiService,) { }

    /**
     * The function fetches data from an API endpoint using the provided request parameters and returns a
     * promise that resolves with the data and a success flag.
     * @param {string} endpoint - The `endpoint` parameter is a string that represents the API endpoint or
     * URL that you want to fetch data from. It typically specifies the specific resource or action you
     * want to perform on the server.
     * @param {any} requestParams - The `requestParams` parameter is an object that contains the parameters
     * to be sent with the API request. These parameters can include things like query parameters, request
     * headers, or request body data. The specific structure and content of the `requestParams` object will
     * depend on the API you are working with and
     * @returns The fetch function is returning a Promise that resolves to an object with two properties:
     * "data" and "success". The "data" property can hold any value, while the "success" property is a
     * boolean indicating whether the API request was successful or not.
     */
    fetch(endpoint: string, requestParams: any): Promise<{ data: any, success: boolean }> {
        return new Promise<{ data: any, success: boolean }>((resolve, reject) => {
            this.apiService[endpoint](requestParams)
                .then(data => {
                    resolve({ data, success: true });
                })
                .catch(error => {
                    resolve({ data: null, success: false });
                });
        });
    }

    /**
     * The function `getGlobalLanguages` returns an array of objects containing the uppercase label and
     * lowercase value of each language.
     * @returns The function `getGlobalLanguages()` returns an array of objects. Each object has two
     * properties: `label` and `value`. The `label` property is the uppercase version of a language code,
     * and the `value` property is the language code itself.
     */
    getGlobalLanguages() {
        const languages = ['en', 'es', 'fr', 'it', 'pt', 'sl', 'sq'];
        const mappedLanguages = languages.map(lang => ({ label: lang.toUpperCase(), value: lang }));
        return mappedLanguages;
    }

    /**
     * The `formatData` function takes in an array of headers and an array of records, and returns an
     * object containing the header-field relationship and the formatted records.
     * @param {string[]} headers - An array of strings representing the headers of the data. Each string
     * represents a column header.
     * @param {string[][]} records - The `records` parameter is an array of arrays of strings. Each inner
     * array represents a record, and each string represents a value for a specific field in that record.
     * @returns The function `formatData` returns an object with two properties: `headerFieldRelationship`
     * and `formattedRecords`.
     */
    formatData(headers: string[], records: string[][]): any {
        const headerFieldRelationship = headers.map(header => ({ header, field: header.toLowerCase().replace(/\s+/g, '').replace(".", '') }));
        const formattedRecords = records.map(record => {
            const formattedRecord: any = {};
            headerFieldRelationship.forEach((relationship, index) => {
                formattedRecord[relationship.field] = record[index];
            });
            return formattedRecord;
        });

        return { headerFieldRelationship, formattedRecords };
    }



    /**
     * The function calculates the number of rows per page based on the total data length and the desired
     * number of rows per page.
     * @param {number} totalDataLength - The total length of the data that needs to be displayed.
     * @param {number} rowsPerPage - The number of rows to display per page in a table or data grid.
     * @returns the number of rows per page. If the total data length is greater than the specified rows
     * per page, it will return the rows per page value. Otherwise, it will return the total data length.
     */
    calculateRowsPerPage(totalDataLength: number, rowsPerPage: number): number {
        return totalDataLength > rowsPerPage ? rowsPerPage : totalDataLength;
    }

    /**
     * The `extractColumns` function recursively extracts columns from nested objects based on a filter and
     * returns an array of column objects with headers and field names.
     * @param {any} data - The `data` parameter is the object from which you want to extract columns.
     * @param {string} [parentKey] - The `parentKey` parameter is an optional string that represents the
     * parent key of the current object being processed. It is used to build the full path of the current
     * object by concatenating it with the current key.
     * @param {{ name: string, value: string }[]} [colsFilter] - An optional array of objects that specify
     * the columns to be extracted from the data. Each object in the array should have two properties:
     * @returns The function `extractColumns` returns an array of objects. Each object in the array
     * represents a column and has two properties: `header` and `field`.
     */
    extractColumns(data: any, parentKey?: string, colsFilter?: { name: string, value: string }[]): any[] {
        let columns: any[] = [];
        Object.keys(data).forEach(key => {
            const fullPath = parentKey ? `${parentKey}.${key}` : key;
            if (typeof data[key] === 'object' && data[key] !== null) {
                // Se aplica recursividad para llamar a objetos anidados
                columns = columns.concat(this.extractColumns(data[key], fullPath, colsFilter));
            } else {
                // Si es común y está en colsFilter, lo añade
                const filterItem = colsFilter?.find(item => item.value === fullPath);
                if (filterItem) {
                    columns.push({
                        header: filterItem.name,
                        field: fullPath
                    });
                }
            }
        });

        // Ordenar las columnas según el filtro
        if (colsFilter) {
            columns.sort((a, b) => {
                const indexA = colsFilter.findIndex(item => item.value === a.field);
                const indexB = colsFilter.findIndex(item => item.value === b.field);
                return indexA - indexB;
            });
        }

        return columns;
    }

    /**
     * The function "capitalizeLastElement" takes a string as input, splits it into parts using the dot as
     * a delimiter, and returns the last part with the first letter capitalized and the rest of the letters
     * in lowercase.
     * @param {string} str - The `str` parameter is a string that represents a sentence or a phrase.
     * @returns The function `capitalizeLastElement` returns a string with the last element of the input
     * string capitalized.
     */
    capitalizeLastElement(str: string): string {
        const parts = str.split('.');
        const lastPart = parts[parts.length - 1];
        return lastPart.charAt(0).toUpperCase() + lastPart.slice(1).toLowerCase();
    }

    /**
     * The function `getNestedValue` retrieves a nested value from an object based on a given field string.
     * @param {any} obj - The `obj` parameter is the object from which you want to retrieve the nested
     * value.
     * @param {string} field - The `field` parameter is a string that represents the nested property you
     * want to retrieve from the `obj` object. The nested property is specified using dot notation, where
     * each dot represents a level of nesting. For example, if you have an object `obj` with the following
     * structure:
     * @returns The function `getNestedValue` returns the value of the nested field in the given object. If
     * the value is null or not defined, it returns a space character (' ').
     */
    getNestedValue(obj: any, field: string): any {
        const fields = field.split('.');
        let value = obj;
        for (const f of fields) {
            if (value && typeof value === 'object' && f in value) {
                value = value[f];
            } else {
                return ' '; // Devuelve espacio en blanco si el valor es null o no está definido
            }
        }
        return value;
    }

    getNestedValueDynamic(obj: any, field: string): any {
        if (!obj || !field) return ' '; // Devuelve espacio en blanco si el objeto o el campo no están definidos

        const fields = field.split('.');
        let value = obj;

        for (const f of fields) {
            if (value && typeof value === 'object' && f in value) {
                value = value[f];
            } else {
                return ' '; // Devuelve espacio en blanco si el valor es null o no está definido
            }
        }

        return value;
    }

}

